import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  Loader2, Save, Send, Copy, Printer, Trash2, Plus, ArrowLeft, Check, XCircle, Link as LinkIcon,
  Eye, FileDown, RefreshCcw, CalendarPlus, AlertTriangle,
} from "lucide-react";
import {
  getProposalForAdmin, updateProposal, replaceLineItems, replaceColors,
  sendProposal, setProposalStatus, generateProposalPdf, extendProposalExpiration, resendProposal,
} from "@/lib/proposals.functions";
import { ColorLookup } from "@/components/proposals/ColorLookup";

const PROJECT_TYPES = [
  { value: "interior", label: "Interior" },
  { value: "exterior", label: "Exterior" },
  { value: "cabinets", label: "Cabinets" },
  { value: "deck", label: "Deck / Fence" },
  { value: "commercial", label: "Commercial" },
  { value: "other", label: "Other" },
];

export const Route = createFileRoute("/_authenticated/admin/proposals/$id")({
  component: ProposalEditor,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="py-20 text-center">
        <p className="text-red-400 mb-3">{error.message}</p>
        <button onClick={() => { router.invalidate(); reset(); }} className="bg-white text-zinc-950 px-5 py-2 rounded-full text-sm font-semibold">Try again</button>
      </div>
    );
  },
  notFoundComponent: () => <div className="text-zinc-400">Not found.</div>,
});

type LineItem = { id?: string; title: string; description?: string | null; area_or_quantity?: number | null; unit?: string | null; unit_price?: number | null; total: number; sort_order: number };
type ColorRow = { id?: string; surface: string; color_name?: string | null; color_number?: string | null; hex?: string | null; lrv?: number | null; product_line?: string | null; sheen?: string | null; coats: number; sort_order: number };

const fmtMoney = (n: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

function ProposalEditor() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const getFn = useServerFn(getProposalForAdmin);
  const updateFn = useServerFn(updateProposal);
  const replaceItemsFn = useServerFn(replaceLineItems);
  const replaceColorsFn = useServerFn(replaceColors);
  const sendFn = useServerFn(sendProposal);
  const statusFn = useServerFn(setProposalStatus);
  const pdfFn = useServerFn(generateProposalPdf);
  const extendFn = useServerFn(extendProposalExpiration);
  const resendFn = useServerFn(resendProposal);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "proposal", id],
    queryFn: () => getFn({ data: { id } }),
  });

  const [form, setForm] = useState<any>(null);
  const [items, setItems] = useState<LineItem[]>([]);
  const [colors, setColors] = useState<ColorRow[]>([]);
  const [scopeText, setScopeText] = useState("");
  const [copied, setCopied] = useState(false);
  const [sendStatus, setSendStatus] = useState<string | null>(null);
  const [overrideOpen, setOverrideOpen] = useState<null | "approved" | "declined">(null);
  const [overrideReason, setOverrideReason] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    if (!data) return;
    setForm({ ...data.proposal });
    setItems(data.line_items.map((i: any, idx: number) => ({ ...i, sort_order: idx })));
    setColors(data.colors.map((c: any, idx: number) => ({ ...c, sort_order: idx })));
    setScopeText(((data.proposal.scope as string[] | null) || []).join("\n"));
  }, [data]);

  const subtotal = useMemo(() => items.reduce((s, i) => s + Number(i.total || 0), 0), [items]);
  const total = Math.max(0, subtotal - Number(form?.discount || 0));
  const depositAmount = form?.deposit_required
    ? form.deposit_percentage
      ? Math.round((total * Number(form.deposit_percentage)) / 100)
      : Number(form.deposit_amount || 0)
    : 0;

  const saveAllMut = useMutation({
    mutationFn: async () => {
      const scope = scopeText.split("\n").map((s) => s.trim()).filter(Boolean);
      await replaceItemsFn({ data: { proposal_id: id, items } });
      await replaceColorsFn({ data: { proposal_id: id, colors } });
      await updateFn({
        data: {
          id,
          patch: {
            customer_name: form.customer_name,
            customer_email: form.customer_email,
            customer_phone: form.customer_phone,
            property_address: form.property_address,
            project_type: form.project_type,
            estimated_timeline: form.estimated_timeline,
            scope,
            warranty: form.warranty,
            terms: form.terms,
            notes: form.notes,
            discount: Number(form.discount || 0),
            deposit_required: !!form.deposit_required,
            deposit_percentage: form.deposit_percentage != null ? Number(form.deposit_percentage) : null,
            deposit_amount: depositAmount,
            expires_at: form.expires_at,
          },
        },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "proposal", id] }),
  });

  const sendMut = useMutation({
    mutationFn: async () => {
      await saveAllMut.mutateAsync();
      return sendFn({ data: { id, origin: window.location.origin } });
    },
    onSuccess: (r) => {
      setSendStatus(r.email?.ok ? "Email sent." : r.email?.skipped ? "Saved as sent (no email key configured)." : `Sent. Email status: ${r.email?.error || "unknown"}`);
      qc.invalidateQueries({ queryKey: ["admin", "proposal", id] });
    },
    onError: (e) => setSendStatus((e as Error).message),
  });

  const setStatusMut = useMutation({
    mutationFn: (vars: { status: any; reason?: string }) => statusFn({ data: { id, status: vars.status, reason: vars.reason } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "proposal", id] }),
    onError: (e) => setSendStatus((e as Error).message),
  });

  const extendMut = useMutation({
    mutationFn: (days: number) => extendFn({ data: { id, days } }),
    onSuccess: (r) => {
      setSendStatus(`Expiration extended to ${new Date(r.expires_at).toLocaleDateString()}.`);
      qc.invalidateQueries({ queryKey: ["admin", "proposal", id] });
    },
  });

  const resendMut = useMutation({
    mutationFn: () => resendFn({ data: { id, origin: window.location.origin } }),
    onSuccess: (r) => {
      setSendStatus(r.email?.ok ? "Reminder email sent." : r.email?.skipped ? "Resent (no email key — see diagnostics)." : `Resend failed: ${r.email?.error}`);
      qc.invalidateQueries({ queryKey: ["admin", "proposal", id] });
    },
  });

  async function downloadPdf() {
    if (pdfBusy) return;
    setPdfBusy(true);
    try {
      const r = await pdfFn({ data: { id } });
      const bin = atob(r.base64);
      const u8 = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
      const blob = new Blob([u8], { type: r.mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = r.filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      setSendStatus(`PDF failed: ${(e as Error).message}`);
    } finally {
      setPdfBusy(false);
    }
  }

  async function printPdf() {
    if (pdfBusy) return;
    setPdfBusy(true);
    try {
      const r = await pdfFn({ data: { id } });
      const bin = atob(r.base64);
      const u8 = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
      const blob = new Blob([u8], { type: r.mimeType });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank");
      if (w) {
        w.addEventListener("load", () => { try { w.focus(); w.print(); } catch {} });
      } else {
        setSendStatus("Please allow pop-ups to print the PDF.");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setSendStatus(`PDF failed: ${(e as Error).message}`);
    } finally {
      setPdfBusy(false);
    }
  }

  if (isLoading || !form) {
    return <div className="py-16 grid place-items-center"><Loader2 className="size-5 animate-spin text-zinc-500" /></div>;
  }

  const publicLink = typeof window !== "undefined" ? `${window.location.origin}/proposal/${form.public_token}` : "";
  const isDraft = form.status === "draft";
  const isExpired = form.status === "expired" || (form.expires_at && new Date(form.expires_at) < new Date());

  return (
    <div className="max-w-6xl space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link to="/admin/proposals" className="text-xs text-zinc-500 hover:text-amber-300 inline-flex items-center gap-1">
            <ArrowLeft className="size-3" /> All proposals
          </Link>
          <h1 className="font-serif text-3xl text-white mt-1">Proposal {form.proposal_number}</h1>
          <p className="text-xs text-zinc-500 mt-1">Status: <span className="text-amber-300 capitalize">{form.status.replace("_"," ")}</span></p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => { navigator.clipboard.writeText(publicLink); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
            className="text-xs px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-zinc-200 hover:bg-white/10 inline-flex items-center gap-1.5">
            {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />} Copy public link
          </button>
          {isDraft ? (
            <Link to="/admin/proposals/$id/preview" params={{ id }} target="_blank"
              className="text-xs px-3 py-2 rounded-lg bg-white/5 border border-amber-300/30 text-amber-200 hover:bg-amber-300/10 inline-flex items-center gap-1.5">
              <Eye className="size-3.5" /> Admin preview (draft)
            </Link>
          ) : (
            <a href={publicLink} target="_blank" rel="noreferrer"
              className="text-xs px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-zinc-200 hover:bg-white/10 inline-flex items-center gap-1.5">
              <LinkIcon className="size-3.5" /> Preview
            </a>
          )}
          <button onClick={downloadPdf} disabled={pdfBusy}
            className="text-xs px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-zinc-200 hover:bg-white/10 inline-flex items-center gap-1.5 disabled:opacity-60">
            {pdfBusy ? <Loader2 className="size-3.5 animate-spin" /> : <FileDown className="size-3.5" />} PDF
          </button>
          <button onClick={printPdf} disabled={pdfBusy}
            className="text-xs px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-zinc-200 hover:bg-white/10 inline-flex items-center gap-1.5 disabled:opacity-60">
            <Printer className="size-3.5" /> Print
          </button>
          <button onClick={() => saveAllMut.mutate()} disabled={saveAllMut.isPending}
            className="text-xs px-3 py-2 rounded-lg bg-white/10 border border-white/15 text-white inline-flex items-center gap-1.5 hover:bg-white/15 disabled:opacity-60">
            {saveAllMut.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />} Save
          </button>
          <button onClick={() => sendMut.mutate()} disabled={sendMut.isPending}
            className="text-xs px-3 py-2 rounded-full bg-amber-300 text-zinc-950 font-semibold inline-flex items-center gap-1.5 hover:bg-amber-200 disabled:opacity-60">
            {sendMut.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Send className="size-3.5" />} Save & Send
          </button>
        </div>
      </header>

      {sendStatus && <div className="text-xs px-3 py-2 rounded-lg border border-amber-300/30 bg-amber-300/10 text-amber-100">{sendStatus}</div>}

      {/* Expiration / resend toolbar */}
      {!isDraft && (
        <section className="rounded-xl border border-white/10 bg-white/[0.02] p-3 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-zinc-400">
            {isExpired ? <span className="text-red-300 inline-flex items-center gap-1"><AlertTriangle className="size-3.5" /> Expired</span> : "Active"} ·
            {form.expires_at ? ` expires ${new Date(form.expires_at).toLocaleDateString()}` : " no expiration"}
          </span>
          <div className="ml-auto flex flex-wrap gap-2">
            {[7, 14, 30].map((d) => (
              <button key={d} onClick={() => extendMut.mutate(d)} disabled={extendMut.isPending}
                className="px-2.5 py-1.5 rounded-md bg-white/5 border border-white/10 text-zinc-200 hover:bg-white/10 inline-flex items-center gap-1 disabled:opacity-60">
                <CalendarPlus className="size-3" /> +{d}d
              </button>
            ))}
            <button onClick={() => resendMut.mutate()} disabled={resendMut.isPending || !form.customer_email}
              className="px-2.5 py-1.5 rounded-md bg-white/10 border border-white/15 text-white hover:bg-white/15 inline-flex items-center gap-1 disabled:opacity-60">
              {resendMut.isPending ? <Loader2 className="size-3 animate-spin" /> : <RefreshCcw className="size-3" />} Resend email
            </button>
          </div>
        </section>
      )}

      {/* Status quick actions */}
      <section className="flex flex-wrap gap-2 items-center">
        {(["sent","changes_requested","void"] as const).map((s) => (
          <button key={s} onClick={() => setStatusMut.mutate({ status: s })}
            className={`text-[11px] px-3 py-1.5 rounded-full border capitalize ${form.status === s ? "bg-amber-300 text-zinc-950 border-amber-300 font-semibold" : "bg-white/5 text-zinc-300 border-white/10 hover:border-amber-300/40"}`}>
            Mark {s.replace("_"," ")}
          </button>
        ))}
        <button onClick={() => { setOverrideReason(""); setOverrideOpen("approved"); }}
          className="text-[11px] font-semibold px-3 py-1.5 rounded-full border border-emerald-500 text-white bg-emerald-600 hover:bg-emerald-500 shadow-sm">
          Override → approved
        </button>
        <button onClick={() => { setOverrideReason(""); setOverrideOpen("declined"); }}
          className="text-[11px] font-semibold px-3 py-1.5 rounded-full border border-red-500 text-white bg-red-600 hover:bg-red-500 shadow-sm">
          Override → declined
        </button>
        <span className="text-[11px] text-zinc-500 inline-flex items-center gap-1 ml-2">
          <AlertTriangle className="size-3" /> Manual overrides require a reason and are audit-logged.
        </span>
      </section>

      {/* Manual override modal */}
      {overrideOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4">
          <div className="bg-zinc-950 border border-amber-300/30 rounded-2xl max-w-md w-full p-5">
            <h3 className="text-white font-serif text-lg mb-1">Manual admin override</h3>
            <p className="text-xs text-zinc-400 mb-3">
              You are about to mark this proposal as <strong className="text-amber-300">{overrideOpen}</strong> on behalf of the customer.
              This bypasses the customer's signature flow. The override will be permanently audit-logged with your admin user ID and timestamp.
            </p>
            <label className="text-xs text-zinc-300 block">
              Reason (required, min 3 chars)
              <textarea value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} rows={4}
                placeholder="e.g. Customer confirmed approval via phone call on 6/30 with Mike."
                className="mt-1 w-full bg-black/40 border border-white/10 rounded-lg p-2.5 text-sm text-zinc-100" />
            </label>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setOverrideOpen(null)} className="text-xs px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-zinc-300">Cancel</button>
              <button
                disabled={overrideReason.trim().length < 3 || setStatusMut.isPending}
                onClick={() => {
                  setStatusMut.mutate({ status: overrideOpen, reason: overrideReason.trim() }, {
                    onSuccess: () => setOverrideOpen(null),
                  });
                }}
                className="text-xs px-4 py-2 rounded-full bg-amber-300 text-zinc-950 font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
                {setStatusMut.isPending ? <Loader2 className="size-3.5 animate-spin" /> : null} Confirm override
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* LEFT: editor */}
        <div className="lg:col-span-2 space-y-6">
          <Card title="Customer">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Name" value={form.customer_name} onChange={(v) => setForm({ ...form, customer_name: v })} />
              <Field label="Email" value={form.customer_email ?? ""} onChange={(v) => setForm({ ...form, customer_email: v })} />
              <Field label="Phone" value={form.customer_phone ?? ""} onChange={(v) => setForm({ ...form, customer_phone: v })} />
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-zinc-500">Project type</span>
                <select
                  value={form.project_type ?? "interior"}
                  onChange={(e) => setForm({ ...form, project_type: e.target.value })}
                  className="mt-1 w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-100"
                >
                  {PROJECT_TYPES.map((p) => (
                    <option key={p.value} value={p.value} className="bg-zinc-900">{p.label}</option>
                  ))}
                </select>
              </label>
              <Field label="Property address" value={form.property_address ?? ""} onChange={(v) => setForm({ ...form, property_address: v })} className="sm:col-span-2" />
              <Field label="Estimated timeline" value={form.estimated_timeline ?? ""} onChange={(v) => setForm({ ...form, estimated_timeline: v })} />
              <Field label="Expires (ISO date)" value={form.expires_at ? new Date(form.expires_at).toISOString().slice(0,10) : ""}
                onChange={(v) => setForm({ ...form, expires_at: v ? new Date(v).toISOString() : null })} type="date" />
            </div>
          </Card>

          <Card title="Scope of work" hint="One bullet per line.">
            <textarea value={scopeText} onChange={(e) => setScopeText(e.target.value)} rows={9}
              className="w-full bg-black/30 border border-white/10 rounded-lg p-3 text-sm text-zinc-100" />
          </Card>

          <Card title="Paint schedule" right={
            <button onClick={() => setColors([...colors, { surface: "Walls", color_name: "", color_number: "", hex: "#ffffff", sheen: "Eggshell", product_line: "", coats: 2, sort_order: colors.length }])}
              className="text-xs inline-flex items-center gap-1 text-amber-300 hover:text-amber-200"><Plus className="size-3.5" /> Add color</button>
          }>
            <div className="space-y-2">
              {colors.length === 0 && <p className="text-xs text-zinc-500">No colors selected. Add one above.</p>}
              {colors.map((c, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-black/20 border border-white/10 rounded-lg p-2">
                  <input className="col-span-2 bg-black/30 border border-white/10 rounded px-2 py-1.5 text-xs text-zinc-100" placeholder="Surface" value={c.surface} onChange={(e) => updateAt(colors, setColors, idx, { surface: e.target.value })} />
                  <div className="col-span-5">
                    <ColorLookup
                      value={{ color_name: c.color_name, color_number: c.color_number, hex: c.hex }}
                      onPick={(picked) => updateAt(colors, setColors, idx, {
                        color_name: picked.color_name,
                        color_number: picked.color_number,
                        hex: picked.hex,
                        lrv: picked.lrv,
                        product_line: picked.product_line,
                      })}
                    />
                  </div>
                  <div className="col-span-1 flex items-center gap-1">
                    <input type="color" className="w-7 h-7 rounded border border-white/10 bg-transparent" value={c.hex || "#ffffff"} onChange={(e) => updateAt(colors, setColors, idx, { hex: e.target.value })} />
                  </div>
                  <input className="col-span-2 bg-black/30 border border-white/10 rounded px-2 py-1.5 text-xs text-zinc-100" placeholder="Sheen" value={c.sheen ?? ""} onChange={(e) => updateAt(colors, setColors, idx, { sheen: e.target.value })} />
                  <input type="number" min={1} max={5} className="col-span-1 bg-black/30 border border-white/10 rounded px-2 py-1.5 text-xs text-zinc-100" value={c.coats} onChange={(e) => updateAt(colors, setColors, idx, { coats: Number(e.target.value) || 1 })} />
                  <button onClick={() => setColors(colors.filter((_, i) => i !== idx))} className="col-span-1 text-zinc-500 hover:text-red-400 justify-self-end"><Trash2 className="size-3.5" /></button>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Line items" right={
            <button onClick={() => setItems([...items, { title: "New item", total: 0, sort_order: items.length }])}
              className="text-xs inline-flex items-center gap-1 text-amber-300 hover:text-amber-200"><Plus className="size-3.5" /> Add line</button>
          }>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-black/20 border border-white/10 rounded-lg p-2">
                  <input className="col-span-4 bg-black/30 border border-white/10 rounded px-2 py-1.5 text-xs text-zinc-100" placeholder="Title" value={it.title} onChange={(e) => updateAt(items, setItems, idx, { title: e.target.value })} />
                  <input className="col-span-5 bg-black/30 border border-white/10 rounded px-2 py-1.5 text-xs text-zinc-100" placeholder="Description" value={it.description ?? ""} onChange={(e) => updateAt(items, setItems, idx, { description: e.target.value })} />
                  <input type="number" className="col-span-2 bg-black/30 border border-white/10 rounded px-2 py-1.5 text-xs text-zinc-100 tabular-nums text-right" placeholder="Total" value={it.total} onChange={(e) => updateAt(items, setItems, idx, { total: Number(e.target.value) || 0 })} />
                  <button onClick={() => setItems(items.filter((_, i) => i !== idx))} className="col-span-1 text-zinc-500 hover:text-red-400 justify-self-end"><Trash2 className="size-3.5" /></button>
                </div>
              ))}
              {items.length === 0 && <p className="text-xs text-zinc-500">No line items.</p>}
            </div>
          </Card>

          <Card title="Warranty">
            <textarea value={form.warranty ?? ""} onChange={(e) => setForm({ ...form, warranty: e.target.value })} rows={4}
              className="w-full bg-black/30 border border-white/10 rounded-lg p-3 text-sm text-zinc-100" />
          </Card>

          <Card title="Terms & conditions">
            <textarea value={form.terms ?? ""} onChange={(e) => setForm({ ...form, terms: e.target.value })} rows={6}
              className="w-full bg-black/30 border border-white/10 rounded-lg p-3 text-sm text-zinc-100" />
          </Card>

          <Card title="Internal notes (not shown to customer)">
            <textarea value={form.notes ?? ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3}
              className="w-full bg-black/30 border border-white/10 rounded-lg p-3 text-sm text-zinc-100" />
          </Card>
        </div>

        {/* RIGHT: totals + deposit + images + activity */}
        <aside className="space-y-6">
          <Card title="Pricing">
            <div className="space-y-2 text-sm">
              <Row label="Subtotal" value={fmtMoney(subtotal)} />
              <div className="flex items-center justify-between">
                <span className="text-zinc-400 text-xs">Discount</span>
                <input type="number" value={form.discount ?? 0} onChange={(e) => setForm({ ...form, discount: Number(e.target.value) || 0 })}
                  className="w-24 text-right bg-black/30 border border-white/10 rounded px-2 py-1 text-xs text-zinc-100 tabular-nums" />
              </div>
              <div className="border-t border-white/10 pt-2 flex items-center justify-between">
                <span className="text-white font-semibold">Total</span>
                <span className="text-amber-300 font-serif text-xl">{fmtMoney(total)}</span>
              </div>
            </div>
          </Card>

          <Card title="Deposit">
            <label className="flex items-center gap-2 text-xs text-zinc-300 mb-2">
              <input type="checkbox" checked={!!form.deposit_required} onChange={(e) => setForm({ ...form, deposit_required: e.target.checked })} />
              Require deposit
            </label>
            {form.deposit_required && (
              <div className="space-y-2">
                <Field label="Deposit %" value={String(form.deposit_percentage ?? "")} type="number"
                  onChange={(v) => setForm({ ...form, deposit_percentage: v === "" ? null : Number(v) })} />
                <p className="text-xs text-zinc-400">Deposit due: <span className="text-amber-300 font-semibold">{fmtMoney(depositAmount)}</span></p>
              </div>
            )}
          </Card>

          <Card title="Images">
            {form.studio_render_url && <img src={form.studio_render_url} alt="Render" className="rounded-lg border border-white/10 mb-2" />}
            {form.design_image_url && <img src={form.design_image_url} alt="Design" className="rounded-lg border border-white/10 mb-2" />}
            {form.cover_image_url && <img src={form.cover_image_url} alt="Cover" className="rounded-lg border border-white/10 mb-2" />}
            {!(form.studio_render_url || form.design_image_url || form.cover_image_url) && (
              <p className="text-xs text-zinc-500">No images attached.</p>
            )}
          </Card>

          <Card title="Activity">
            <ul className="space-y-2 text-xs">
              {data!.activity.length === 0 && <li className="text-zinc-500">No activity yet.</li>}
              {data!.activity.map((a: any) => (
                <li key={a.id} className="border-l-2 border-amber-300/30 pl-3">
                  <p className="text-zinc-200 capitalize">{a.activity_type.replace("_"," ")}</p>
                  {a.note && <p className="text-zinc-500">{a.note}</p>}
                  <p className="text-zinc-400">{new Date(a.created_at).toLocaleString()}</p>
                </li>
              ))}
            </ul>
          </Card>

          {data!.change_requests.length > 0 && (
            <Card title="Change requests">
              <ul className="space-y-2 text-xs">
                {data!.change_requests.map((r: any) => (
                  <li key={r.id} className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-2">
                    <p className="text-orange-200 font-semibold">{r.customer_name || "Customer"}</p>
                    <p className="text-zinc-300 mt-1 whitespace-pre-wrap">{r.message}</p>
                    <p className="text-zinc-500 mt-1">{new Date(r.created_at).toLocaleString()}</p>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </aside>
      </div>
    </div>
  );
}

function Card({ title, right, hint, children }: { title: string; right?: React.ReactNode; hint?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      <header className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-white font-semibold text-sm">{title}</h3>
          {hint && <p className="text-[11px] text-zinc-500 mt-0.5">{hint}</p>}
        </div>
        {right}
      </header>
      {children}
    </section>
  );
}
function Field({ label, value, onChange, type = "text", className = "" }: { label: string; value: string; onChange: (v: string) => void; type?: string; className?: string }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-100" />
    </label>
  );
}
function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-zinc-400 text-xs">{label}</span>
      <span className="text-zinc-100 tabular-nums">{value}</span>
    </div>
  );
}
function updateAt<T>(arr: T[], setArr: (v: T[]) => void, idx: number, patch: Partial<T>) {
  const copy = arr.slice();
  copy[idx] = { ...copy[idx], ...patch };
  setArr(copy);
}